import Axios from 'axios';
import Decimal from 'decimal.js';
import { createInstance } from 'sharetribe-flex-sdk';
import defaultConfig from '../../config/configDefault';
import appSettings from '../../config/settings';
import * as apiUtils from '../../util/api';
import { types as sdkTypes, transit } from '../../util/sdkLoader';
import { environment, port } from './config/common';
import getJwtToken from './mod/jwt/getJwtToken';

// Only this sdk to check authInfo
const sdk = createInstance({
  transitVerbose: appSettings.sdk.transitVerbose,
  clientId: appSettings.sdk.clientId,
  secure: appSettings.usingSSL,
  typeHandlers: apiUtils.typeHandlers,
});

/**
 * Get the base URL of the API
 *
 * @param {string} [marketplaceRootURL]
 * @returns {string} API base URL
 */
const apiBaseUrl = (marketplaceRootURL) => {
  const useDevApiServer = environment === 'development' && !!port;

  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  return marketplaceRootURL?.replace(/\/$/, '') ?? '';
};

const typeHandlers = [
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: (v) => new sdkTypes.BigDecimal(v.toString()),
    reader: (v) => new Decimal(v.value),
  },
];

/**
 * @param {any} data
 * @returns {string} Serialized data
 */
const serialize = (data) => {
  return transit.write(data, {
    typeHandlers,
    verbose: appSettings.sdk.transitVerbose,
  });
};

/**
 * @param {string} str
 * @returns {any} Deserialized data
 */
const deserialize = (str) => {
  return transit.read(str, { typeHandlers });
};

/**
 *
 * @param {Object} response - Axios response object
 * @returns {any} Parsed response data
 */
const parseResponseData = (response) => {
  const { data, headers } = response;
  const contentType = headers['Content-Type'] ?? headers['content-type'];
  if (contentType?.includes('application/transit+json')) {
    return deserialize(data);
  }
  if (contentType?.includes('application/json')) {
    return JSON.parse(data);
  }
  return data;
};

const axios = Axios.create({
  baseURL: apiBaseUrl(defaultConfig.marketplaceRootURL),
});

axios.interceptors.response.use(
  (response) => {
    const data = parseResponseData(response);
    response.data = data;
    return response;
  }
  // No rejection handler: an Error built here would stack from the interceptor
  // down through axios, with no frame from whoever called the API. request()
  // shapes it instead, where the caller's stack is still reachable.
);

// Three response shapes reach here and each hides its real message somewhere
// different: server/api-util/sdk.js `handleError` nests the specific one under
// data.errors and puts a generic 'Local API request failed' at the top level;
// sendUnauthorized and the marketplace API put theirs at errors[0]. Hence the
// order — the envelope's own message is the least useful of the three.
const reasonFrom = (body, fallback) =>
  body?.data?.errors?.[0]?.message || body?.errors?.[0]?.message || body?.message || fallback;

// A non-JSON body (a proxy's HTML 502) must not be spread onto the error:
// Object.assign copies a string's indices as own properties. A body that won't
// parse must not replace the failure either.
const parsedBodyOf = (axiosError) => {
  if (!axiosError.response) return {};
  try {
    const body = parseResponseData(axiosError.response);
    return body && typeof body === 'object' && !Array.isArray(body) ? body : {};
  } catch {
    return {};
  }
};

const apiError = (axiosError, callSite, method, url) => {
  const body = parsedBodyOf(axiosError);
  const status = body.status ?? axiosError.response?.status;
  const statusText = body.statusText ?? axiosError.response?.statusText;

  const e = new Error();
  // Keeps the body's own keys (status, statusText, data, name) on the error, as
  // callers have always seen them.
  Object.assign(e, body);
  e.status ??= status;
  e.statusText ??= statusText;
  e.message = [
    `${method || 'GET'} ${url}`,
    [status, statusText].filter(Boolean).join(' '),
    reasonFrom(body, axiosError.message),
  ]
    .filter(Boolean)
    .join(' - ');

  // An Error built in the interceptor stacks from the interceptor down through
  // axios internals, with no frame from whoever called the API — which is the
  // one thing worth knowing. callSite was captured before the request, so it
  // still has them.
  const frames = callSite.stack?.split('\n').slice(1).join('\n');
  if (frames) {
    e.stack = `${e.name}: ${e.message}\n${frames}`;
  }
  e.cause = axiosError;

  return e;
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

const formatBody = (headers, body) => {
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;

  if (shouldSerializeBody) {
    return serialize(body);
  }
  return body;
};

export const request = async (path, options = {}) => {
  const {
    credentials = 'include',
    headers = { 'Content-Type': 'application/transit+json' },
    body,
    method,
    ...rest
  } = options;

  const bodyMaybe = body ? { data: formatBody(headers, body) } : {};

  // Captured before the await chain, while the caller is still on the stack.
  const callSite = new Error();

  const authInfo = await sdk.authInfo();

  const { isAnonymous } = authInfo;

  if (!isAnonymous) {
    const token = await getJwtToken();
    headers.jauthorization = `Bearer ${token}`;
  }

  try {
    const response = await axios.request({
      url: path,
      method,
      headers,
      withCredentials: credentials === 'include',
      responseType: 'text',
      ...bodyMaybe,
      ...rest,
    });

    return response.data;
  } catch (e) {
    throw apiError(e, callSite, method, path);
  }
};

export const getMethod = (path, query, options = {}) => {
  const queryParams = new URLSearchParams(query);
  const pathWithQuery = `${path}?${queryParams}`;

  return request(pathWithQuery, { ...options, method: methods.GET });
};

export const requestMethodWithBody =
  (method) =>
  (path, body, options = {}) => {
    return request(path, { ...options, body, method });
  };

export const downloadMethod = (path, query, options = {}) => {
  const queryParams = new URLSearchParams(query);
  const pathWithQuery = `${path}?${queryParams}`;

  const url = `${apiBaseUrl()}${pathWithQuery}`;
  const { credentials, headers, body, filename, ...rest } = options;

  const fetchOptions = {
    credentials: credentials || 'include',
    headers: headers || { Accept: '*/*' },
    ...rest,
  };

  return window
    .fetch(url, fetchOptions)
    .then((res) => {
      if (!res.ok) {
        throw new Error('Network response was not ok');
      }
      return res.blob();
    })
    .then((blob) => {
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    });
};

export const postMethod = requestMethodWithBody(methods.POST);
export const patchMethod = requestMethodWithBody(methods.PATCH);
export const putMethod = requestMethodWithBody(methods.PUT);
export const deleteMethod = requestMethodWithBody(methods.DELETE);
